import React, { useState } from "react";
import { CHART_1, CHART_2, CHART_3, INK, MUTED, GRID, PAPER } from "./palette";

// ---- discriminant_axes: everyone's scores on the first two discriminant functions, by group ----
//
// Props (ChartData#discriminant_axes): groups [{ key, name, n, points [[f1, f2]], centroid [f1, f2] }],
// functions [{ eigenvalue, share, canonicalR, raw, standardized, structure }] (the first two),
// outcomes [{ name, structure [s1, s2] }], remaining { chi2, df, p } (Bartlett's test of the functions
// after the first), arrows (draw each outcome's structure coefficients from the origin), legendTitle, noun.
// Scores are centered on the grand mean and scaled so each function's within-group variance is 1, so
// both axes share one unit (a within-group SD) and are drawn to the same scale.
//
// Group colors, centroid dots, the grand-mean cross, legend and tooltip match GroupEllipsesChart.jsx,
// so the same groups look the same on both charts; keep them in step with it.

const COLORS = [CHART_1, CHART_2, CHART_3];
const ARROW_SCALE = 2; // structure coefficients are correlations (|r| ≤ 1); doubled so they read against scores of ±2

const W = 640;
const M = { top: 16, right: 16, left: 16 };

const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
const fmtR = (x, places = 2) => fmt(x, places).replace(/^(−?)0\./, "$1.");
const fmtP = (p) => (p < 0.001 ? "< .001" : `= ${fmtR(p, 3)}`);
const minus = (t) => (t < 0 ? `−${-t}` : t);
const pct = (x) => `${Math.round(x * 100)}%`;
const lowerWords = (s) => s.split(" ").map((w) => (/^[A-Z][a-z]+$/.test(w) ? w.toLowerCase() : w)).join(" ");
const pair = ([a, b]) => `(${fmt(a)}, ${fmt(b)})`;
const article = (w) => `${/^[aeiou]/i.test(w) ? "an" : "a"} ${w}`;
const listing = (parts) => (parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`);

// Ticks every `step` between lo and hi.
function ticksFor(lo, hi, step) {
	const out = [];
	for (let t = Math.ceil(lo / step - 1e-9) * step; t <= hi + 1e-9; t += step) out.push(+t.toFixed(10));
	return out;
}

function spread(items, gap = 14) {
	const sorted = [...items].sort((a, b) => a.y - b.y);
	sorted.forEach((it, i) => { if (i > 0 && it.y - sorted[i - 1].y < gap) it.y = sorted[i - 1].y + gap; });
	return items;
}

const Cross = ({ cx, cy, size = 6, color = INK, width = 2 }) => (
	<g stroke={color} strokeWidth={width} strokeLinecap="round">
		<line x1={cx - size} x2={cx + size} y1={cy - size} y2={cy + size} />
		<line x1={cx - size} x2={cx + size} y1={cy + size} y2={cy - size} />
	</g>
);

function Legend({ items, title }) {
	const swatch = (kind, color) => {
		switch (kind) {
			case "ring": return <span className="inline-block h-2.5 w-2.5 rounded-full border-[1.5px]" style={{ borderColor: INK }} />;
			case "arrow": return <svg width="16" height="10" viewBox="0 0 16 10" aria-hidden="true"><line x1="1" y1="5" x2="11" y2="5" stroke={color} strokeWidth="1.75" /><path d="M10,1.5 L15,5 L10,8.5 Z" fill={color} /></svg>;
			case "cross": return <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><Cross cx={5} cy={5} size={3.5} width={1.75} /></svg>;
			default: return <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color }} />;
		}
	};
	return (
		<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
			{title && <span className="font-semibold text-gray-700">{title}</span>}
			{items.map(({ label, color, kind }) => (
				<span key={label} className="flex items-center gap-1.5">
					{swatch(kind, color)}
					{label}
				</span>
			))}
		</div>
	);
}

function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, M.left), W - M.right - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${M.top})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

// What one function does to the groups: with three groups, if the middle centroid sits near one end,
// the function sets the other end's group apart; otherwise it runs from the lowest to the highest.
function separation(groups, d, noun) {
	const sorted = [...groups].sort((a, b) => a.centroid[d] - b.centroid[d]);
	const lo = sorted[0], hi = sorted[sorted.length - 1], range = hi.centroid[d] - lo.centroid[d];
	const at = (g) => `${g.name.toLowerCase()} (${fmt(g.centroid[d])})`;
	if (sorted.length === 3) {
		const mid = sorted[1];
		if (mid.centroid[d] - lo.centroid[d] < range / 4) return `sets ${hi.name.toLowerCase()} ${noun}s apart (${fmt(hi.centroid[d])}, against ${fmt(lo.centroid[d])} and ${fmt(mid.centroid[d])})`;
		if (hi.centroid[d] - mid.centroid[d] < range / 4) return `sets ${lo.name.toLowerCase()} ${noun}s apart (${fmt(lo.centroid[d])}, against ${fmt(mid.centroid[d])} and ${fmt(hi.centroid[d])})`;
	}
	return `runs from ${at(lo)} to ${at(hi)}`;
}

// What one function measures, from the outcomes' structure coefficients on it.
function reading(outcomes, d) {
	const sorted = [...outcomes].sort((a, b) => Math.abs(b.structure[d]) - Math.abs(a.structure[d]));
	const top = sorted[0], name = lowerWords(top.name);
	const coefficients = sorted.map((o, i) => `${lowerWords(o.name)}${i === 0 ? " correlates" : ""} ${fmtR(o.structure[d])}${i === 0 ? " with it" : ""}`);
	const rest = sorted.slice(1);
	let gist = `mostly ${name}${top.structure[d] < 0 ? ", reversed" : ""}`;
	// An outcome that barely correlates with the function, beside one that dominates it, has been held constant.
	if (outcomes.length === 2 && Math.abs(rest[0].structure[d]) < 0.1)
		gist = `${name} with ${lowerWords(rest[0].name)}'s share taken out: higher scores mean ${top.structure[d] < 0 ? "less" : "more"} ${name} than ${lowerWords(rest[0].name)} would predict`;
	return { coefficients: listing(coefficients), gist };
}

export default function DiscriminantAxesChart({ groups, functions, outcomes = [], remaining, arrows = true, legendTitle, noun = "participant" }) {
	const [hover, setHover] = useState(null);
	const id = React.useId().replace(/:/g, "");
	const all = groups.flatMap((g) => g.points);
	const tips = arrows ? outcomes.map((o) => o.structure.map((s) => s * ARROW_SCALE)) : [];
	const xs = [...all.map((p) => p[0]), ...tips.map((t) => t[0])], ys = [...all.map((p) => p[1]), ...tips.map((t) => t[1])];
	// One unit is one within-group SD on both axes, so both get the same pixels per unit.
	const lo = [Math.min(...xs) - 0.3, Math.min(...ys) - 0.3], hi = [Math.max(...xs) + 0.3, Math.max(...ys) + 0.3];
	const box = { l: 44, r: W - 150, t: M.top + 18 };
	const unit = (box.r - box.l) / (hi[0] - lo[0]);
	box.b = box.t + unit * (hi[1] - lo[1]);
	const height = box.b + 40;
	const x = (v) => box.l + (v - lo[0]) * unit, y = (v) => box.b - (v - lo[1]) * unit;
	const total = groups.reduce((a, g) => a + g.n, 0);
	const gl = lowerWords(legendTitle || "group");
	const [one, two] = functions;
	const xLabel = `Function 1 (${pct(one.share)} of the separation)`, yLabel = `Function 2 (${pct(two.share)})`;
	const labels = spread(groups.map((g, i) => ({ i, y: y(g.centroid[1]) + 4 })), 30);
	const dim = (i) => (hover && hover.i !== undefined && hover.i !== i ? 0.25 : 1);
	const read = [0, 1].map((d) => reading(outcomes, d));
	const sep = [0, 1].map((d) => separation(groups, d, noun));
	return (
		<figure>
			<Legend title={legendTitle} items={[
				...groups.map((g, i) => ({ label: g.name, color: COLORS[i % 3] })),
				{ label: "Group centroid", kind: "ring" },
				{ label: "Grand mean", kind: "cross" },
				...(arrows ? [{ label: `Outcome's correlations with the functions (×${ARROW_SCALE})`, color: MUTED, kind: "arrow" }] : [])]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`${total} ${noun}s' scores on the first two discriminant functions, by ${gl}. Function 1, ${pct(one.share)} of the separation, ${sep[0]}; function 2, ${pct(two.share)}, ${sep[1]}. Centroids (function 1, function 2): ${groups.map((g) => `${g.name} ${pair(g.centroid)}`).join(", ")}.${outcomes.length ? ` Structure coefficients: ${outcomes.map((o) => `${lowerWords(o.name)} ${fmtR(o.structure[0])} and ${fmtR(o.structure[1])}`).join("; ")}.` : ""}`}>
				<defs>
					<marker id={`${id}-arrow`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
						<path d="M0,0 L10,5 L0,10 Z" fill={MUTED} />
					</marker>
				</defs>
				{ticksFor(lo[1], hi[1], 1).map((t) => (
					<g key={`y${t}`}>
						<line x1={box.l} x2={box.r} y1={y(t)} y2={y(t)} stroke={GRID} strokeDasharray={t === 0 ? "4 3" : undefined} strokeWidth={t === 0 ? 1.5 : 1} />
						<text x={box.l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{minus(t)}</text>
					</g>
				))}
				{ticksFor(lo[0], hi[0], 1).map((t) => (
					<g key={`x${t}`}>
						<line x1={x(t)} x2={x(t)} y1={box.t} y2={box.b} stroke={GRID} strokeDasharray={t === 0 ? "4 3" : undefined} strokeWidth={t === 0 ? 1.5 : 1} />
						<text x={x(t)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{minus(t)}</text>
					</g>
				))}
				<text x={box.r} y={box.b + 32} fontSize="11" fill={MUTED} textAnchor="end">{xLabel}</text>
				<text x={box.l} y={box.t - 8} fontSize="11" fill={MUTED}>{yLabel}</text>
				{groups.map((g, i) => g.points.map(([a, b], j) => (
					<circle key={`${g.key}-${j}`} cx={x(a)} cy={y(b)} r="3.5" fill={COLORS[i % 3]} stroke={PAPER} strokeWidth="1" opacity={0.8 * dim(i)}
						onMouseEnter={() => setHover({ i, cx: x(a), lines: [`One ${noun}, ${g.name}`, `Function 1: ${fmt(a)}`, `Function 2: ${fmt(b)}`] })} onMouseLeave={() => setHover(null)} />
				)))}
				{arrows && outcomes.map((o, k) => {
					const [tx, ty] = tips[k], len = Math.hypot(tx, ty) || 1;
					// Beyond the tip, or above it when the arrow is nearly flat, so the label stays off the axis labels.
					const flat = Math.abs(ty / len) < 0.3;
					const lx = flat ? x(tx) : x(tx) + (tx / len) * 8, ly = flat ? y(ty) - 12 : y(ty) - (ty / len) * 8;
					return (
						<g key={`a${o.name}`} opacity={hover && hover.i !== undefined ? 0.4 : 1}
							onMouseEnter={() => setHover({ cx: x(tx), lines: [`${o.name}: its correlation with each function, within ${gl}s`, `Function 1: r = ${fmtR(o.structure[0])}`, `Function 2: r = ${fmtR(o.structure[1])}`, `Arrow drawn at ${ARROW_SCALE}× its length`] })} onMouseLeave={() => setHover(null)}>
							<line x1={x(0)} y1={y(0)} x2={x(tx)} y2={y(ty)} stroke="transparent" strokeWidth="12" />
							<line x1={x(0)} y1={y(0)} x2={x(tx)} y2={y(ty)} stroke={MUTED} strokeWidth="1.75" markerEnd={`url(#${id}-arrow)`} />
							<text x={lx} y={ly} fontSize="11" fill={INK} fontWeight="600" dominantBaseline="middle"
								textAnchor={flat ? (tx < 0 ? "start" : "end") : Math.abs(tx / len) < 0.3 ? "middle" : tx < 0 ? "end" : "start"}>{o.name}</text>
						</g>
					);
				})}
				{groups.map((g, i) => (
					<g key={`m${g.key}`} onMouseEnter={() => setHover({ i, cx: x(g.centroid[0]), lines: [`${g.name}: the centroid of ${g.n} ${noun}s`, `Function 1: ${fmt(g.centroid[0])}`, `Function 2: ${fmt(g.centroid[1])}`] })} onMouseLeave={() => setHover(null)}>
						<circle cx={x(g.centroid[0])} cy={y(g.centroid[1])} r="12" fill="transparent" />
						<circle cx={x(g.centroid[0])} cy={y(g.centroid[1])} r="7" fill={COLORS[i % 3]} stroke={INK} strokeWidth="1.5" />
					</g>
				))}
				<g onMouseEnter={() => setHover({ cx: x(0), lines: [`Grand mean of all ${total} ${noun}s`, "Function 1: 0", "Function 2: 0"] })} onMouseLeave={() => setHover(null)}>
					<circle cx={x(0)} cy={y(0)} r="10" fill="transparent" />
					<Cross cx={x(0)} cy={y(0)} />
				</g>
				{groups.map((g, i) => (
					<g key={`l${g.key}`}>
						<circle cx={box.r + 15} cy={labels[i].y - 10} r="5" fill={COLORS[i % 3]} stroke={INK} strokeWidth="1" />
						<text x={box.r + 25} y={labels[i].y - 6} fontSize="12" fill={INK} fontWeight="600">{g.name}</text>
						<text x={box.r + 25} y={labels[i].y + 8} fontSize="11" fill={MUTED}>{pair(g.centroid)}</text>
					</g>
				))}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each small dot is one {noun}, placed by their scores on the two discriminant functions; each large dot is {article(gl)}'s centroid, and the cross is the grand mean, at 0 on both.
				Both functions are scaled so scores have an SD of 1 within each {gl}, so the axes share one unit.
				Function 1 ({pct(one.share)} of the separation, canonical r = {fmtR(one.canonicalR)}) {sep[0]}{outcomes.length ? <>; {read[0].coefficients}, so it is {read[0].gist}</> : null}.
				Function 2 ({pct(two.share)}, r = {fmtR(two.canonicalR)}) {sep[1]}{outcomes.length ? <>; {read[1].coefficients}, so it is {read[1].gist}</> : null}.
				{remaining && <> Bartlett's test of what's left after function 1 gives χ²({remaining.df}) = {remaining.chi2.toFixed(2)}, p {fmtP(remaining.p)}; {remaining.p < 0.05 ? "the data chose function 2's weights, so treat it as exploratory" : "the data can't tell function 2's separation from none"}.</>}
				{arrows && outcomes.length > 0 && <> Each arrow points the way {noun}s higher on that outcome lie; it ends at the outcome's correlations with the two functions, drawn at {ARROW_SCALE === 2 ? "twice" : `${ARROW_SCALE} times`} their length.</>}
			</figcaption>
		</figure>
	);
}
